import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { RegistryRow, ValueRow } from '../types/bsc';
import { isoDateToUtcTimestamp } from '../lib/dates';
import { formatAxisValue, formatDisplayValue } from '../lib/formatters';
import { toNumber } from '../lib/numbers';

interface HistoryChartProps {
  rows: ValueRow[];
  registry: RegistryRow;
  lineColors: Record<string, string>;
  multiline: boolean;
}

interface ChartPoint {
  timestamp: number;
  period: string;
  [dimension: string]: string | number | null;
}

interface ThresholdLine {
  key: string;
  value: number;
  label: string;
  stroke: string;
  dash: string;
}

function buildChartData(rows: ValueRow[]): { points: ChartPoint[]; dimensions: string[] } {
  const dimensions = [...new Set(rows.map((row) => row.dimension_value))].sort((a, b) =>
    a.localeCompare(b, 'it'),
  );
  const pointsByPeriod = new Map<string, ChartPoint>();

  for (const row of rows) {
    if (row.value === null) continue;
    const point = pointsByPeriod.get(row.period) ?? {
      timestamp: isoDateToUtcTimestamp(row.period),
      period: row.period,
    };
    point[row.dimension_value] = row.value;
    pointsByPeriod.set(row.period, point);
  }

  const points = [...pointsByPeriod.values()].sort(
    (a, b) => Number(a.timestamp) - Number(b.timestamp),
  );
  return { points, dimensions };
}

function buildThresholdLines(registry: RegistryRow): ThresholdLine[] {
  const target = toNumber(registry.Target);
  if (target === null) return [];

  const minimum = toNumber(registry.Soglia_Minima);
  const excellence = toNumber(registry.Soglia_Eccellenza);
  const trend = String(registry.Trend_Desiderato ?? '')
    .trim()
    .toLocaleLowerCase('it-IT');

  const lines: ThresholdLine[] = [
    {
      key: 'target',
      value: target,
      label: 'Target',
      stroke: '#334155',
      dash: '6 5',
    },
  ];

  if (['stabile', 'stabilità', 'stabilita'].includes(trend)) {
    if (minimum !== null) {
      const delta = Math.abs(minimum);
      lines.push(
        {
          key: 'minimum-upper',
          value: target + delta,
          label: 'Soglia minima +',
          stroke: '#ad1818',
          dash: '3 5',
        },
        {
          key: 'minimum-lower',
          value: target - delta,
          label: 'Soglia minima −',
          stroke: '#ad1818',
          dash: '3 5',
        },
      );
    }
    if (excellence !== null) {
      const delta = Math.abs(excellence);
      lines.push(
        {
          key: 'excellence-upper',
          value: target + delta,
          label: 'Eccellenza +',
          stroke: '#17922a',
          dash: '3 5',
        },
        {
          key: 'excellence-lower',
          value: target - delta,
          label: 'Eccellenza −',
          stroke: '#17922a',
          dash: '3 5',
        },
      );
    }
    return lines;
  }

  if (minimum !== null) {
    lines.push({
      key: 'minimum',
      value: minimum,
      label: 'Soglia minima',
      stroke: '#ad1818',
      dash: '3 5',
    });
  }
  if (excellence !== null) {
    lines.push({
      key: 'excellence',
      value: excellence,
      label: 'Eccellenza',
      stroke: '#17922a',
      dash: '3 5',
    });
  }
  return lines;
}

function formatTimestamp(value: unknown): string {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return String(value ?? '');
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(timestamp));
}

function formatQuarterTimestamp(value: unknown): string {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return '';
  const date = new Date(timestamp);
  const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
  return `Q${quarter} ${date.getUTCFullYear()}`;
}

export function HistoryChart({ rows, registry, lineColors, multiline }: HistoryChartProps) {
  const { points, dimensions } = buildChartData(rows);
  const thresholds = buildThresholdLines(registry);

  if (points.length === 0 || dimensions.length === 0) {
    return <div className="chart-empty">Nessuna serie storica disponibile.</div>;
  }

  return (
    <div className="history-chart" role="img" aria-label={`Serie storica del KPI ${registry.Codice}`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 24, right: 28, bottom: 12, left: 12 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="timestamp"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatQuarterTimestamp}
            tickLine={false}
            minTickGap={26}
          />
          <YAxis
            tickFormatter={(value: unknown) =>
              formatAxisValue(Number(value), registry.Unità_di_Misura)
            }
            tickLine={false}
            axisLine={false}
            width={92}
          />
          <Tooltip
            labelFormatter={formatTimestamp}
            formatter={(value: unknown, name: unknown) => [
              formatDisplayValue(value, registry.Unità_di_Misura),
              String(name),
            ]}
          />
          {multiline && dimensions.length > 1 ? (
            <Legend
              verticalAlign="bottom"
              wrapperStyle={{ paddingTop: 14 }}
              formatter={(value: unknown) => (
                <span className="chart-legend-label">{String(value)}</span>
              )}
            />
          ) : null}

          {thresholds.map((line) => (
            <ReferenceLine
              key={line.key}
              y={line.value}
              stroke={line.stroke}
              strokeDasharray={line.dash}
              ifOverflow="extendDomain"
              label={{
                value: line.label,
                position: 'insideTopLeft',
                fill: line.stroke,
                fontSize: 11,
              }}
            />
          ))}

          {dimensions.map((dimension) => (
            <Line
              key={dimension}
              type="monotone"
              dataKey={dimension}
              name={dimension}
              stroke={lineColors[dimension] ?? '#1d76c0'}
              strokeWidth={2.5}
              connectNulls={false}
              dot={{ r: 3, strokeWidth: 1 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
